import { ComponentProps } from 'react';
import { Chip } from 'melp-design/components';
import {
  AccessLevel,
  accessLevels,
  PermissionName,
  Role,
} from './permissions.types';

export const CHIP_BY_ACCESS_LEVEL: Record<
  AccessLevel,
  NonNullable<ComponentProps<typeof Chip>['variant']>
> = {
  0: 'neutral',
  1: 'info',
  2: 'success',
};

/**
 * Maps a selected role to the admin create/update payload fields.
 * System roles are identified by `roleType` alone; custom roles also need `customRoleId`.
 */
export const getAdminRolePayload = (role: Pick<Role, 'id' | 'roleType'>) => {
  if (role.roleType === 'custom') {
    return { roleType: role.roleType, customRoleId: role.id };
  }

  return { roleType: role.roleType };
};

/**
 * Resolves the i18n key for an access level label. Some permissions rename
 * specific levels (e.g. `employees` shows "masked" instead of "none" for
 * level 0), so the label depends on both the access level and the permission.
 */
export const getAccessLevelLabelKey = (
  accessLevel: AccessLevel,
  permission?: PermissionName,
) => {
  if (permission === 'employees' && accessLevel === 0) {
    return 'permissions.accessLevel.masked' as const;
  }

  return `permissions.accessLevel.${accessLevel}` as const;
};

/**
 * Some permissions only support a subset of access levels. Anything not listed
 * here supports the full range (`none`, `view`, `full`).
 */
const ACCESS_LEVELS_BY_PERMISSION: Partial<
  Record<PermissionName, readonly AccessLevel[]>
> = {
  groups: [1, 2],
  melpShopOrders: [0, 1],
  companies: [0, 1],
  generalSettings: [0, 1],
  integrations: [0, 1],
  administrators: [0, 1],
  transmissionActs: [0, 1],
  systemLog: [0],
};

/**
 * Returns the access levels a permission can be set to. Used to build the
 * editable options; a single option means the permission is not configurable.
 */
export const getAccessLevelOptions = (
  permission: PermissionName,
): readonly AccessLevel[] => {
  return ACCESS_LEVELS_BY_PERMISSION[permission] ?? accessLevels;
};
